<script setup lang="ts">
import { ItinerarySegmentPlaceholder } from '@apps/main/new-desktop/modules/itineraryButton';

import { AppFlicker, AppTrashCanButton } from '@libs/ui';

import type { ItinerarySimpleTransportSegment } from '../../models';
import {
  getTransportTypeForItinerarySimpleTransportSegment,
  getTransportSegmentPlaceholderForItinerarySimpleTransportSegment,
} from '../../stores/tripOverviewSegments/tripOverviewStoreSegmentsLogic';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
} from '../../stores';

import ItineraryTransportSelector from './ItineraryTransportSelector.vue';
import ItineraryTimeline from './ItineraryTimeline.vue';

interface ItinerarySimpleTransportSegmentProps {
  segment: ItinerarySimpleTransportSegment;
  printMode: boolean;
  shouldHideProductLink: boolean;
}

const props = defineProps<ItinerarySimpleTransportSegmentProps>();

const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();

function removeTransportSegment(): void {
  tripOverviewModalsStore.confirmRemoveSimpleTransport(props.segment);
}
</script>

<template>
  <ItineraryTimeline :segment-type="segment.segmentType">
    <template #dropdown>
      <ItineraryTransportSelector
        v-if="!props.shouldHideProductLink"
        :transport-type="
          getTransportTypeForItinerarySimpleTransportSegment(
            props.segment.segmentType
          )
        "
        :disabled="props.printMode"
        @select-transport="
          tripOverviewPageStore.selectTransport(segment, $event)
        "
      />
      <ItinerarySegmentPlaceholder
        v-else
        :transport-type="
          getTransportSegmentPlaceholderForItinerarySimpleTransportSegment(
            props.segment.segmentType
          )
        "
      />
    </template>
    <template #header>
      <div class="itinerary-simple-transport-segment">
        <div
          v-if="tripOverviewPageStore.isLoadingSimpleTransportSegmentDetails"
          class="transport-details-loader"
        >
          <AppFlicker
            height="spacing-12"
            width="spacing-48"
            border-radius="border-radius-8"
          />
          <AppFlicker
            height="spacing-12"
            width="spacing-48"
            border-radius="border-radius-8"
          />
        </div>

        <div v-else-if="props.segment.transport" class="transport-details">
          <div>{{ props.segment.transport.distance }}</div>
          <div>{{ props.segment.transport.time }}</div>
        </div>
      </div>
      <div
        v-if="!props.printMode && props.shouldHideProductLink"
        class="icon-container-header"
      >
        <div class="icon-container">
          <AppTrashCanButton
            class="icon"
            :aria-label="$t('a11y.remove_transport_segment')"
            @click="removeTransportSegment"
          />
        </div>
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.itinerary-simple-transport-segment {
  display: flex;
  align-items: center;
  gap: var(--spacing-32);
  margin-left: var(--spacing-16);

  .transport-message {
    @include font-label-s-light;
    white-space: nowrap;
  }

  .transport-details,
  .transport-details-loader {
    display: flex;
    gap: var(--spacing-32);
    @include font-label-s-light;
    white-space: nowrap;
  }
}
.icon-container-header {
  width: 100%;
  display: flex;
  justify-content: end;
  .icon-container {
    margin-right: var(--spacing-16);
    .icon {
      color: var(--color-neutral-70);
    }
  }
}
</style>
